import find from 'lodash/find';
import get from 'lodash/get';
import flow from 'lodash/flow';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import groupBy from 'lodash/groupBy';
import transform from 'lodash/transform';

/**
 * @desc  SummaryService class.
 * @ngInject
 */
export default class SummaryService {
  /**
   * Constructor of the class.
   *
   * @param {$rootScope} $rootScope
   * @param {$q} $q
   * @param {$log}  $log
   * @param {$http} $http
   * @param {moment} moment
   * @param {EmployeeService} EmployeeService
   * @param {UtilService} UtilService
   */
  constructor(
    $rootScope, $q, $log, $http, moment, EmployeeService, UtilService
  ) {
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.$log = $log;
    this.$http = $http;
    this.moment = moment;
    this.EmployeeService = EmployeeService;
    this.UtilService = UtilService;
  }

  getEmployeesAbsences(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/employeesAbsence`,
      params
    }).then(result => {
      return this.$q.resolve(result.data);
    }, err => this.$q.reject(err))
      .then(result => this.transformEmployeesAbsences(result));
  }

  getTotalEmployeesAbsence(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/totalEmployeesAbsence`,
      params
    }).then(result => {
      return result.data;
    }, err => this.$q.reject(err))
      .then(result => this.transformTotalEmployeesAbsence(result));
  }

  getStudentsPrecence(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/studentsPresence`,
      params
    }).then(result => {
      return result.data;
    }, err => this.$q.reject(err))
      .then(result => this.transformStudentsPresence(result));
  }

  getStudentsPrecenceGroupByDate(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/studentsPresenceGroupByDate`,
      params
    })
      .then(({data}) => data)
      .catch(err => this.$q.reject(err));
  }

  getStudentPresenceToDayChart(params) {
    return this.getStudentsPrecenceGroupByDate(params)
      .then(result => this.transformPresenceToDayChart(result.stats, params.dateFrom, params.dateTo));
  }

  getStudentPresenceToMonthChart(params) {
    return this.getStudentsPrecenceGroupByDate(params)
      .then(result => this.transformPresenceToMonthChart(result.stats, params.dateFrom, params.dateTo));
  }

  getUnrealizedActivities(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/unrealizedActivities`,
      params
    })
      .then(({data}) => data, err => this.$q.reject(err))
      .then(result => this.addEmployeeData(result))
      .then(result => this.transformUnrealizedActivities(result));
  }

  getMyUnrealizedActivities(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/me/summary/${this.$rootScope.me.schoolCommonRoomId}/unrealizedActivities`,
      params
    })
      .then(({data}) => data, err => this.$q.reject(err))
      .then(result => this.addEmployeeData(result))
      .then(result => this.transformUnrealizedActivities(result));
  }

  getRealizedActivities(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/summary/${this.$rootScope.me.schoolCommonRoomId}/realizedActivities`,
      params
    })
      .then(({data}) => data, err => this.$q.reject(err))
      .then(result => this.addEmployeeData(result))
      .then(result => this.transformRealizedActivities(result));
  }

  getMyRealizedActivities(params) {
    return this.$http({
      method: 'GET',
      url: `/SCR/me/summary/${this.$rootScope.me.schoolCommonRoomId}/realizedActivities`,
      params
    })
      .then(({data}) => data, err => this.$q.reject(err))
      .then(result => this.addEmployeeData(result))
      .then(result => this.transformRealizedActivities(result));
  }

  transformEmployeesAbsences({summary, total}) {
    return this.EmployeeService.getSchoolCommonRoomEmployees()
      .then(employees => ({
        summary: summary.map(item => ({
          ...item,
          fullName: flow([
            () => find(employees, ['id', item.employeeId]),
            result => get(result, 'fullName', 'Błąd - nie można odnaleźć nauczyciela')
          ])(employees),
          presenceHours: this.moment().utc().startOf('day').add(item.workingTimeInMinutes, 'minutes').format('HH:mm'),
          workingDayHours: this.moment().utc().startOf('day').add(item.plannedWorkingTimeInMinutes, 'minutes').format('HH:mm'),
          absenceHours: this.moment().utc().startOf('day').add(item.plannedWorkingTimeInMinutes - item.workingTimeInMinutes, 'minutes').format('HH:mm')
        })),
        total
      }));
  }

  transformTotalEmployeesAbsence({
    numberOfEmployees,
    plannedWorkingTimeInMinutes,
    workingTimeInMinutes
  }) {
    const absenceTimeInMinutes = plannedWorkingTimeInMinutes - workingTimeInMinutes;
    return {
      numberOfEmployees,
      plannedWorkingTime: this.UtilService.minutesToString(plannedWorkingTimeInMinutes),
      workingTime: this.UtilService.minutesToString(workingTimeInMinutes),
      absenceTime: this.UtilService.minutesToString(absenceTimeInMinutes),
      absencePercent: `${Math.round(((absenceTimeInMinutes / plannedWorkingTimeInMinutes) * 100) || 0)}%`
    };
  }

  transformStudentsPresence({
    summary,
    totalStudentsPresenceTime,
    totalNumberStudents,
    total
  }) {
    return {
      summary: summary.map(student => ({
        ...student,
        studentId: Number(student.studentId),
        groupName: student.groupName || '',
        presenceTimeInMinutes: Number(student.presenceTimeInMinutes),
        presenceHours: this.moment().utc().startOf('day').add(student.presenceTimeInMinutes, 'minutes').format('HH:mm'),
        fullName: `${student.lastName} ${student.name}`
      })),
      totalStudentsPresenceTime: this.UtilService.minutesToString(totalStudentsPresenceTime),
      totalNumberStudents: Number(totalNumberStudents),
      total: Number(total)
    };
  }

  transformPresenceToDayChart(stats, dateFrom, dateTo) {
    const dateRange = [
      this.moment(dateFrom).format('YYYY-MM-DD'),
      this.moment(dateTo).format('YYYY-MM-DD')
    ];
    const resultValueMap = flow([
      data => keyBy(data, 'date'),
      data => mapValues(data, 'studentsNumber')
    ])(stats);

    return this.UtilService.getFullWeeks(dateRange).map(item => ({
      date: item,
      studentsNumber: resultValueMap[item] || 0
    }));
  }

  transformPresenceToMonthChart(stats, dateFrom, dateTo) {
    const dateRange = [
      this.moment(dateFrom).format('YYYY-MM'),
      this.moment(dateTo).format('YYYY-MM')
    ];

    const grouped = groupBy(stats, obj => {
      return this.moment(obj.date).format('YYYY-MM');
    });

    const reduced = transform(grouped, (result, value, key) => {
      result[key] = value.reduce((prev, curr) => {
        return prev + curr.studentsNumber;
      }, 0);
      return result;
    }, {});

    return this.UtilService
      .fillDateRange(dateRange, 'month')
      .map(item => ({
        date: item,
        studentsNumber: reduced[item] || 0
      }));
  }

  addEmployeeData({
    data,
    total
  }) {
    return this.EmployeeService.getSchoolCommonRoomEmployees()
      .then(employees => ({
        data: data.map(item => ({
          ...item,
          fullName: flow([
            () => find(employees, ['id', item.employeeId]),
            result => get(result, 'fullName', 'Błąd - nie można odnaleźć nauczyciela')
          ])(employees)
        })),
        total
      }));
  }

  transformUnrealizedActivities({
    data,
    total
  }) {
    return {
      data: data.map(({
        date,
        fullName,
        startTime,
        endTime,
        unrealizedTimeRange = []
      }) => ({
        date,
        workingHours: `${startTime} - ${endTime}`,
        fullName,
        lackHours: unrealizedTimeRange.map(({startTime, endTime}) => `${startTime} - ${endTime}`).join(', ')
      })),
      total: Number(total)
    };
  }

  transformRealizedActivities({
    data,
    total
  }) {
    return {
      data: data.map(item => {
        const {plannedWorkingTimeInMinutes, realizedTimeInMinutes} = item;
        return {
          ...item,
          plannedWorkingTimeInHours: this.UtilService.minutesToString(plannedWorkingTimeInMinutes),
          realizedTimeInHours: this.UtilService.minutesToString(realizedTimeInMinutes)
        };
      }),
      total
    };
  }
}
